import React, { useEffect, useState, useRef, createElement } from 'react';
import { MapPinIcon, DollarSignIcon, ShoppingCartIcon, ClockIcon, TrendingUpIcon, UsersIcon, CheckCircleIcon, AlertCircleIcon, TruckIcon, BellIcon, XCircleIcon, CalendarIcon, FilterXIcon, DownloadIcon, CheckIcon, BanIcon, PackageIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { Visit } from '../types';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
export function DashboardPage() {
  const {
    user
  } = useAuth();
  const {
    visits,
    getTodayStats,
    users,
    orders,
    refreshData,
    newOrderAlert
  } = useApp();
  // Date range filter for charts
  const [chartDateFrom, setChartDateFrom] = useState('');
  const [chartDateTo, setChartDateTo] = useState('');
  // ── Retroactive discount map (same logic as VisitsPage) ──────────────
  const [retroDiscountMap, setRetroDiscountMap] = useState<Record<string, number>>({});
  useEffect(() => {
    const computeRetroDiscounts = async () => {
      const cobroVisits = visits.filter(v => (v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta') && v.cobro.huboCobo && v.cobro.montoCobrado > 0 && (v.cobro.descuento || 0) === 0 && !(v.cobro as any)?.anulado);
      if (cobroVisits.length === 0) {
        setRetroDiscountMap({});
        return;
      }
      const visitFactMap: Record<string, string[]> = {};
      const allNums: string[] = [];
      cobroVisits.forEach(v => {
        const fd: any[] = (v.cobro as any).facturasDetalle || [];
        const nums = fd.length > 0 ? fd.map((f: any) => f.numeroFactura).filter(Boolean) : v.cobro.numeroFactura ? v.cobro.numeroFactura.split(',').map((n: string) => n.trim()).filter(Boolean) : [];
        if (nums.length > 0) {
          visitFactMap[v.id] = nums;
          allNums.push(...nums);
        }
      });
      const uniqueNums = [...new Set(allNums)];
      if (uniqueNums.length === 0) return;
      try {
        const {
          data: invRows
        } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', uniqueNums.slice(0, 200));
        if (!invRows) return;
        const ppMap: Record<string, {
          ppPct: number | null;
          subTotal: number;
        }> = {};
        invRows.forEach((row: any) => {
          ppMap[row.numero_factura] = {
            ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
            subTotal: Number(row.sub_total) || 0
          };
        });
        const newMap: Record<string, number> = {};
        Object.entries(visitFactMap).forEach(([visitId, nums]) => {
          const v = visits.find(vis => vis.id === visitId);
          if (!v) return;
          const fd: any[] = (v.cobro as any).facturasDetalle || [];
          let totalDisc = 0;
          nums.forEach(num => {
            const pp = ppMap[num];
            const savedDesc = fd.find((f: any) => f.numeroFactura === num)?.descuento || 0;
            if (savedDesc === 0 && pp && pp.ppPct != null && pp.ppPct > 0 && pp.subTotal > 0) {
              totalDisc += Math.round(pp.subTotal * (pp.ppPct / 100) * 100) / 100;
            }
          });
          if (totalDisc > 0) newMap[visitId] = totalDisc;
        });
        setRetroDiscountMap(newMap);
      } catch {
        // Silently fail
      }
    };
    computeRetroDiscounts();
  }, [visits]);
  // Helper: get effective cobro amount (accounting for saved + retroactive discounts)
  const getEffectiveCobro = (visit: Visit): number => {
    if ((visit.cobro as any)?.anulado) return 0;
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) return 0;
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) {
      const credito = visit.cobro.credito || 0;
      if (credito > 0 && Math.abs(visit.cobro.montoCobrado - credito) < 0.01) {
        return Math.round((visit.cobro.montoCobrado - savedDesc) * 100) / 100;
      }
      return visit.cobro.montoCobrado;
    }
    const retroDisc = retroDiscountMap[visit.id] || 0;
    if (retroDisc > 0) {
      return Math.round((visit.cobro.montoCobrado - retroDisc) * 100) / 100;
    }
    return visit.cobro.montoCobrado;
  };
  const pendingOrders = orders.filter(o => o.estado === 'pendiente');
  const handleApproveOrder = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'procesando'
    }).eq('id', id);
    await refreshData();
  };
  const handleCancelOrder = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'cancelado'
    }).eq('id', id);
    await refreshData();
  };
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Permission helpers for dashboard sections (granular)
  const canSeeCollections = isAdmin || user?.permisos?.dashboard_cobros === true || user?.permisos?.dashboard_cobros === undefined;
  const canSeeSales = isAdmin || user?.permisos?.dashboard_ventas === true || user?.permisos?.dashboard_ventas === undefined;
  const canSeeVisits = isAdmin || user?.permisos?.dashboard_visitas === true || user?.permisos?.dashboard_visitas === undefined;
  const canSeeCharts = isAdmin || user?.permisos?.dashboard_graficos === true || user?.permisos?.dashboard_graficos === undefined;
  const canSeePedidos = isAdmin || user?.permisos?.dashboard_pedidos === true || user?.permisos?.dashboard_pedidos === undefined;
  const canSeeEquipo = isAdmin || user?.permisos?.dashboard_equipo === true;
  const canSeeProductividad = isAdmin || user?.permisos?.dashboard_productividad === true || user?.permisos?.dashboard_productividad === undefined;
  const canSeeEntregas = user?.permisos?.dashboard_entregas === true;
  const isRepartidor = canSeeEntregas && !isAdmin;
  const stats = getTodayStats(isAdmin ? undefined : user?.id);
  const recentVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).slice(0, 6);
  const activeVendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0
    }).format(amount);
  };
  const formatTime = (date: Date | null) => {
    if (!date) return '--:--';
    return new Date(date).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getVisitTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cobro: 'Cobro',
      venta: 'Venta',
      cobro_y_venta: 'Cobro y Venta',
      seguimiento: 'Seguimiento'
    };
    return labels[type] || type;
  };
  const getStatusBadge = (status: string) => {
    if (status === 'completada') return <Badge variant="success" data-id="element-1814">Completada</Badge>;
    if (status === 'en_progreso') return <Badge variant="warning" data-id="element-1815">En Progreso</Badge>;
    return <Badge variant="danger" data-id="element-1816">Cancelada</Badge>;
  };
  const allRelevantVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id);
  // Filtered visits for charts based on date range
  const chartVisits = allRelevantVisits.filter(v => {
    if (chartDateFrom) {
      const visitDate = new Date(v.fecha);
      const fromDate = new Date(chartDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (visitDate < fromDate) return false;
    }
    if (chartDateTo) {
      const visitDate = new Date(v.fecha);
      const toDate = new Date(chartDateTo);
      toDate.setHours(23, 59, 59, 999);
      if (visitDate > toDate) return false;
    }
    return true;
  });
  const hasChartFilter = chartDateFrom || chartDateTo;
  const cobrosBarData = isAdmin ? activeVendors.map(vendor => {
    const vendorVisits = chartVisits.filter(v => v.vendedorId === vendor.id);
    return {
      name: vendor.nombre.split(' ')[0],
      cobrado: vendorVisits.reduce((sum, v) => sum + getEffectiveCobro(v), 0),
      vendido: vendorVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0)
    };
  }) : [];
  const visitsByType = [{
    name: 'Cobro',
    value: chartVisits.filter(v => v.tipoVisita === 'cobro').length,
    color: '#059669'
  }, {
    name: 'Venta',
    value: chartVisits.filter(v => v.tipoVisita === 'venta').length,
    color: '#2952C4'
  }, {
    name: 'Cobro y Venta',
    value: chartVisits.filter(v => v.tipoVisita === 'cobro_y_venta').length,
    color: '#5483df'
  }, {
    name: 'Seguimiento',
    value: chartVisits.filter(v => v.tipoVisita === 'seguimiento').length,
    color: '#d97706'
  }].filter(d => d.value > 0);
  const last7Days = Array.from({
    length: 7
  }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const visitsByDay = last7Days.map(day => {
    const dayVisits = chartVisits.filter(v => new Date(v.fecha).toDateString() === day.toDateString());
    return {
      name: day.toLocaleDateString('es-MX', {
        weekday: 'short',
        day: 'numeric'
      }),
      visitas: dayVisits.length,
      cobrado: dayVisits.reduce((sum, v) => sum + getEffectiveCobro(v), 0) / 1000
    };
  });
  const barChartRef = useRef<HTMLDivElement>(null);
  const pieChartRef = useRef<HTMLDivElement>(null);
  const [isDownloadingBar, setIsDownloadingBar] = useState(false);
  const [isDownloadingPie, setIsDownloadingPie] = useState(false);
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm" data-id="element-1817">
          <p className="font-semibold text-slate-900 mb-1 font-heading" data-id="element-1818">
            {label}
          </p>
          {payload.map((entry: any, index: number) => <p key={index} style={{
          color: entry.color
        }} className="font-medium" data-id="element-1819">
              {entry.name}:{' '}
              {entry.name.includes('cobrad') || entry.name.includes('vendid') ? formatCurrency(entry.value) : entry.value}
            </p>)}
        </div>;
    }
    return null;
  };
  const totalCobradoGeneral = chartVisits.reduce((sum, v) => sum + getEffectiveCobro(v), 0);
  const totalVendidoGeneral = chartVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0);
  const downloadChartPDF = async (chartRef: React.RefObject<HTMLDivElement>, title: string, setLoading: (v: boolean) => void) => {
    if (!chartRef.current) return;
    setLoading(true);
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const ml = 12;
      const mr = 12;
      const cw = pw - ml - mr;
      const genDate = new Date().toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      // ── HEADER BAND (same as reports) ──────────────────────────────────
      const headerH = 36;
      doc.setFillColor(13, 27, 70);
      doc.rect(0, 0, pw, headerH, 'F');
      // Logo
      const logoBase64 = await getLogoBase64();
      if (logoBase64) {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(ml, 5, 26, 26, 3, 3, 'F');
        doc.addImage(logoBase64, 'PNG', ml + 1, 6, 24, 24);
      }
      // Company name
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Miladys Rodriguez & Asoc.', ml + 30, 16);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(123, 163, 216);
      doc.text('Sistema de Control de Vendedores', ml + 30, 23);
      // Date top right
      doc.setFontSize(8);
      doc.setTextColor(123, 163, 216);
      doc.text('Generado: ' + genDate, pw - mr, 16, {
        align: 'right'
      });
      // ── ACCENT BAR ─────────────────────────────────────────────────────
      doc.setFillColor(41, 82, 196);
      doc.rect(0, headerH, pw, 2, 'F');
      // ── TITLE SECTION ──────────────────────────────────────────────────
      let y = headerH + 2;
      const titleStripH = 24;
      doc.setFillColor(248, 250, 255);
      doc.rect(0, y, pw, titleStripH, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(0, y + titleStripH, pw, y + titleStripH);
      y += 6;
      doc.setTextColor(13, 27, 70);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(title.toUpperCase(), ml, y + 5);
      // Period text
      const periodText = chartDateFrom || chartDateTo ? `Período: ${chartDateFrom || 'Inicio'} al ${chartDateTo || 'Hoy'}` : 'Todos los registros';
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(periodText, ml, y + 12);
      // Records pill
      const recordCount = chartVisits.length;
      const pillW = 30;
      doc.setFillColor(41, 82, 196);
      doc.roundedRect(pw - mr - pillW, y, pillW, 8, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.text(recordCount + ' registros', pw - mr - pillW / 2, y + 5.5, {
        align: 'center'
      });
      y = headerH + 2 + titleStripH + 2;
      // ── STATS ROW ─────────────────────────────────────────────────────
      const statsH = 18;
      const statW = cw / 5;
      doc.setFillColor(248, 250, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.rect(ml, y, cw, statsH, 'FD');
      const statsData = [{
        label: 'VISITAS',
        value: String(chartVisits.length),
        r: 13,
        g: 27,
        b: 70
      }, {
        label: 'COMPLETADAS',
        value: String(chartVisits.filter(v => v.estado === 'completada').length),
        r: 13,
        g: 27,
        b: 70
      }, {
        label: 'COBRADO',
        value: formatCurrency(chartVisits.reduce((s, v) => s + getEffectiveCobro(v), 0)),
        r: 5,
        g: 150,
        b: 105
      }, {
        label: 'VENDIDO',
        value: formatCurrency(chartVisits.reduce((s, v) => s + v.venta.montoVenta, 0)),
        r: 41,
        g: 82,
        b: 196
      }, {
        label: 'T. PROMEDIO',
        value: Math.round(chartVisits.filter(v => v.tiempoTotal).reduce((s, v) => s + (v.tiempoTotal || 0), 0) / (chartVisits.filter(v => v.tiempoTotal).length || 1)) + ' min',
        r: 13,
        g: 27,
        b: 70
      }];
      statsData.forEach((stat, i) => {
        const sx = ml + i * statW;
        if (i > 0) {
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.2);
          doc.line(sx, y + 2, sx, y + statsH - 2);
        }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(stat.r, stat.g, stat.b);
        doc.text(stat.value, sx + statW / 2, y + 8, {
          align: 'center'
        });
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(stat.label, sx + statW / 2, y + 14, {
          align: 'center'
        });
      });
      y += statsH + 5;
      // ── SECTION TITLE ──────────────────────────────────────────────────
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(13, 27, 70);
      doc.text('GRÁFICO', ml, y);
      doc.setDrawColor(41, 82, 196);
      doc.setLineWidth(0.6);
      doc.line(ml, y + 1.5, ml + doc.getTextWidth('GRÁFICO'), y + 1.5);
      y += 5;
      // ── CHART IMAGE ────────────────────────────────────────────────────
      const imgW = cw;
      const imgH = canvas.height / canvas.width * imgW;
      const maxH = ph - y - 16;
      const finalH = Math.min(imgH, maxH);
      const finalW = finalH / imgH * imgW;
      // Chart container with border
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(ml, y, finalW, finalH + 4, 2, 2, 'FD');
      doc.addImage(imgData, 'PNG', ml + 2, y + 2, finalW - 4, finalH);
      // ── FOOTER (same as reports) ───────────────────────────────────────
      doc.setFillColor(13, 27, 70);
      doc.rect(0, ph - 9, pw, 9, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(74, 111, 165);
      doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', ml, ph - 3);
      doc.text('Página 1 de 1', pw - mr, ph - 3, {
        align: 'right'
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      const safeName = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      doc.save(`grafico-${safeName}-${dateStr}.pdf`);
    } catch (err) {
      console.error('Error al generar PDF del gráfico:', err);
      alert('Error al generar el PDF: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };
  // Delivery orders for repartidor — only empacado
  const deliveryOrders = orders.filter(o => o.estado === 'empacado');
  const deliveredToday = orders.filter(o => o.estado === 'entregado' && new Date(o.fecha).toDateString() === new Date().toDateString());
  const handleMarkDelivered = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'entregado'
    }).eq('id', id);
    await refreshData();
  };
  const handleCancelDelivery = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'cancelado'
    }).eq('id', id);
    await refreshData();
  };
  return <div className="space-y-6" data-id="element-1820">
      {/* Hero Banner */}
      <div className="rounded-2xl p-4 sm:p-5 lg:p-8 text-white relative overflow-hidden animate-slide-up shadow-lg" style={{
      background: 'linear-gradient(135deg, #0D1B46 0%, #152C6E 50%, #1E3D96 100%)'
    }} data-id="element-1821">
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, #5483df 0%, transparent 50%), radial-gradient(circle at 80% 20%, #2952C4 0%, transparent 40%)'
      }} data-id="element-1822" />
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" style={{
        backgroundColor: 'rgba(41,82,196,0.15)'
      }} data-id="element-1823" />
        <div className="relative" data-id="element-1824">
          <h2 className="text-lg sm:text-xl lg:text-3xl font-bold font-heading" data-id="element-1825">
            ¡Buen día, {user?.nombre.split(' ')[0]}!
          </h2>
          <p className="mt-1 text-blue-200 text-xs sm:text-sm" data-id="element-1826">
            {isAdmin ? 'Resumen de actividad de tu equipo comercial.' : 'Tu resumen de actividad del día.'}
          </p>
          <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-3 lg:flex lg:items-center lg:gap-10" data-id="element-1827">
            {canSeeCollections && <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 lg:bg-transparent lg:p-0" data-id="element-1828">
                <p className="text-blue-300 text-[10px] sm:text-xs lg:text-sm leading-tight" data-id="element-1829">
                  Total Cobrado
                </p>
                <p className="text-sm sm:text-lg lg:text-3xl font-bold text-emerald-400 leading-tight mt-0.5 truncate" data-id="element-1830">
                  {formatCurrency(totalCobradoGeneral)}
                </p>
              </div>}
            {canSeeSales && <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 lg:bg-transparent lg:p-0" data-id="element-1831">
                <p className="text-blue-300 text-[10px] sm:text-xs lg:text-sm leading-tight" data-id="element-1832">
                  Total Vendido
                </p>
                <p className="text-sm sm:text-lg lg:text-3xl font-bold text-blue-300 leading-tight mt-0.5 truncate" data-id="element-1833">
                  {formatCurrency(totalVendidoGeneral)}
                </p>
              </div>}
            {canSeeVisits && <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 lg:bg-transparent lg:p-0" data-id="element-1834">
                <p className="text-blue-300 text-[10px] sm:text-xs lg:text-sm leading-tight" data-id="element-1835">
                  Visitas
                </p>
                <p className="text-sm sm:text-lg lg:text-3xl font-bold text-white leading-tight mt-0.5" data-id="element-1836">
                  {allRelevantVisits.length}
                </p>
              </div>}
          </div>
        </div>
      </div>

      {/* ═══ DELIVERY SECTION (visible for anyone with dashboard_entregas) ═══ */}
      {canSeeEntregas && <>
          {/* Delivery Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-1837">
            <StatsCard title="Por Entregar" value={deliveryOrders.length} icon={<PackageIcon className="w-6 h-6" data-id="element-1839" />} variant="warning" data-id="element-1838" />
            <StatsCard title="Entregados Hoy" value={deliveredToday.length} icon={<CheckCircleIcon className="w-6 h-6" data-id="element-1841" />} variant="success" data-id="element-1840" />
            <StatsCard title="Total Pedidos" value={orders.filter(o => o.estado !== 'cancelado').length} icon={<TruckIcon className="w-6 h-6" data-id="element-1843" />} variant="primary" data-id="element-1842" />
          </div>

          {/* Delivery Orders List */}
          <Card padding="none" data-id="element-1844">
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center" data-id="element-1845">
              <div className="flex items-center gap-3" data-id="element-1846">
                <div className="p-2 bg-amber-50 rounded-lg" data-id="element-1847">
                  <TruckIcon className="w-5 h-5 text-amber-600" data-id="element-1848" />
                </div>
                <div data-id="element-1849">
                  <h3 className="font-bold text-slate-900 font-heading" data-id="element-1850">
                    Pedidos Listos para Entrega
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-1851">
                    Pedidos empacados listos para entregar
                  </p>
                </div>
              </div>
              {newOrderAlert && <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 border border-amber-200 rounded-full text-xs font-bold text-amber-700 animate-pulse" data-id="element-1852">
                  <BellIcon className="w-3.5 h-3.5" data-id="element-1853" />
                  Nuevo pedido empacado
                </span>}
            </div>
            <div className="divide-y divide-slate-100" data-id="element-1854">
              {deliveryOrders.length === 0 ? <div className="px-6 py-12 text-center animate-fade-in" data-id="element-1855">
                  <CheckCircleIcon className="w-10 h-10 text-emerald-300 mx-auto mb-2" data-id="element-1856" />
                  <p className="text-sm text-slate-400 font-medium" data-id="element-1857">
                    Sin entregas pendientes
                  </p>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-1858">¡Buen trabajo!</p>
                </div> : deliveryOrders.map(order => <div key={order.id} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-1859">
                    <div className="flex-1 min-w-0" data-id="element-1860">
                      <div className="flex items-center gap-2" data-id="element-1861">
                        <h4 className="font-semibold text-slate-900 truncate font-heading" data-id="element-1862">
                          {order.farmacia}
                        </h4>
                        <Badge variant={order.estado === 'empacado' ? 'info' : 'warning'} data-id="element-1863">
                          {order.estado === 'empacado' ? 'Empacado' : 'Enviado'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-1864">
                        {new Date(order.fecha).toLocaleDateString('es-DO', {
                  day: 'numeric',
                  month: 'short'
                })}
                        {' · '}
                        {order.numeroPedido}
                        {' · '}
                        <span className="font-medium text-slate-700" data-id="element-1865">
                          {formatCurrency(order.total)}
                        </span>
                        {' · '}
                        {order.items} items
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1866">
                      <button onClick={() => handleMarkDelivered(order.id)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-1867">
                        <CheckIcon className="w-3.5 h-3.5" data-id="element-1868" />
                        Entregado
                      </button>
                      <button onClick={() => handleCancelDelivery(order.id)} className="flex items-center gap-1 px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-semibold rounded-lg transition-colors" data-id="element-1869">
                        <BanIcon className="w-3.5 h-3.5" data-id="element-1870" />
                        Cancelar
                      </button>
                    </div>
                  </div>)}
            </div>
            {/* Recent deliveries */}
            {deliveredToday.length > 0 && <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50" data-id="element-1871">
                <p className="text-xs font-semibold text-slate-500 mb-2" data-id="element-1872">
                  Entregas de hoy
                </p>
                <div className="space-y-1.5" data-id="element-1873">
                  {deliveredToday.slice(0, 5).map(order => <div key={order.id} className="flex items-center justify-between text-sm" data-id="element-1874">
                      <span className="text-slate-600 truncate" data-id="element-1875">
                        {order.farmacia} · {order.numeroPedido}
                      </span>
                      <Badge variant="success" data-id="element-1876">Entregado</Badge>
                    </div>)}
                </div>
              </div>}
          </Card>
        </>}

      {/* ═══ STANDARD DASHBOARD SECTIONS ═══ */}
      {!isRepartidor ? <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-1" data-id="element-1877">
            {canSeeVisits && <StatsCard title="Visitas Hoy" value={stats.totalVisitas} subtitle={`${stats.visitasCompletadas} completadas`} icon={<MapPinIcon className="w-6 h-6" data-id="element-1879" />} variant="primary" data-id="element-1878" />}
            {canSeeCollections && <StatsCard title="Cobrado Hoy" value={formatCurrency(stats.totalCobrado)} icon={<DollarSignIcon className="w-6 h-6" data-id="element-1881" />} variant="success" data-id="element-1880" />}
            {canSeeSales && <StatsCard title="Vendido Hoy" value={formatCurrency(stats.totalVendido)} icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-1883" />} variant="warning" data-id="element-1882" />}
            {canSeeVisits && <StatsCard title="Tiempo Promedio" value={`${stats.tiempoPromedio} min`} subtitle="por visita" icon={<ClockIcon className="w-6 h-6" data-id="element-1885" />} variant="default" data-id="element-1884" />}
          </div>

          {/* Charts date filter + row */}
          {canSeeCharts && <div className="space-y-3 animate-stagger-2" data-id="element-1886">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-id="element-1887">
                <div className="flex items-center gap-2" data-id="element-1888">
                  <div className="p-1.5 bg-[#eef2fc] rounded-lg" data-id="element-1889">
                    <CalendarIcon className="w-4 h-4 text-[#2952C4]" data-id="element-1890" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-700" data-id="element-1891">
                    Rango de fechas para gráficos
                  </h3>
                  {hasChartFilter && <span className="text-xs text-[#2952C4] bg-[#eef2fc] px-2 py-0.5 rounded-full font-medium" data-id="element-1892">
                      Filtrado
                    </span>}
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-1893">
                  <div className="flex items-center gap-1.5 flex-1 sm:flex-initial" data-id="element-1894">
                    <label className="text-xs text-slate-500 font-medium whitespace-nowrap" data-id="element-1895">
                      Desde
                    </label>
                    <input type="date" value={chartDateFrom} onChange={e => setChartDateFrom(e.target.value)} className="w-full sm:w-auto px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1896" />
                  </div>
                  <div className="flex items-center gap-1.5 flex-1 sm:flex-initial" data-id="element-1897">
                    <label className="text-xs text-slate-500 font-medium whitespace-nowrap" data-id="element-1898">
                      Hasta
                    </label>
                    <input type="date" value={chartDateTo} onChange={e => setChartDateTo(e.target.value)} className="w-full sm:w-auto px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1899" />
                  </div>
                  {hasChartFilter && <button onClick={() => {
              setChartDateFrom('');
              setChartDateTo('');
            }} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0" title="Limpiar filtro" data-id="element-1900">
                      <FilterXIcon className="w-4 h-4" data-id="element-1901" />
                    </button>}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1902">
                {(isAdmin || canSeeCollections || canSeeSales) && <Card data-id="element-1903">
                    <div className="flex items-start justify-between gap-2" data-id="element-1904">
                      <CardHeader title={isAdmin ? 'Rendimiento por Vendedor' : 'Actividad Últimos 7 Días'} subtitle={isAdmin ? `Cobros y ventas${hasChartFilter ? ' (filtrado)' : ' acumulados'}` : `Visitas y cobros${hasChartFilter ? ' (filtrado)' : ' diarios'}`} data-id="element-1905" />
                      <button onClick={() => downloadChartPDF(barChartRef, isAdmin ? 'Rendimiento por Vendedor' : 'Actividad Últimos 7 Días', setIsDownloadingBar)} disabled={isDownloadingBar} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors flex-shrink-0 bg-slate-100 text-slate-600 hover:bg-[#eef2fc] hover:text-[#2952C4] disabled:opacity-50 disabled:cursor-not-allowed" title="Descargar gráfico como PDF" data-id="element-1906">
                        {isDownloadingBar ? <div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-1907" /> : <DownloadIcon className="w-3.5 h-3.5" data-id="element-1908" />}
                        <span className="hidden sm:inline" data-id="element-1909">PDF</span>
                      </button>
                    </div>
                    <div className="mt-6 h-64" ref={barChartRef} data-id="element-1910">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-1911">
                        {isAdmin ? <BarChart data={cobrosBarData} barGap={4} data-id="element-1912">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-1913" />
                            <XAxis dataKey="name" tick={{
                    fontSize: 12,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} data-id="element-1914" />
                            <YAxis tick={{
                    fontSize: 11,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-1915" />
                            <Tooltip content={<CustomTooltip data-id="element-1917" />} data-id="element-1916" />
                            <Bar dataKey="cobrado" name="Cobrado" fill="#059669" radius={[6, 6, 0, 0]} data-id="element-1918" />
                            <Bar dataKey="vendido" name="Vendido" fill="#2952C4" radius={[6, 6, 0, 0]} data-id="element-1919" />
                          </BarChart> : <BarChart data={visitsByDay} barGap={4} data-id="element-1920">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-1921" />
                            <XAxis dataKey="name" tick={{
                    fontSize: 12,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} data-id="element-1922" />
                            <YAxis tick={{
                    fontSize: 11,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} data-id="element-1923" />
                            <Tooltip content={<CustomTooltip data-id="element-1925" />} data-id="element-1924" />
                            <Bar dataKey="visitas" name="Visitas" fill="#2952C4" radius={[6, 6, 0, 0]} data-id="element-1926" />
                          </BarChart>}
                      </ResponsiveContainer>
                    </div>
                  </Card>}

                {canSeeVisits && <Card data-id="element-1927">
                    <div className="flex items-start justify-between gap-2" data-id="element-1928">
                      <CardHeader title="Visitas por Tipo" subtitle={`Distribución de actividades${hasChartFilter ? ' (filtrado)' : ''}`} data-id="element-1929" />
                      <button onClick={() => downloadChartPDF(pieChartRef, 'Visitas por Tipo', setIsDownloadingPie)} disabled={isDownloadingPie} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors flex-shrink-0 bg-slate-100 text-slate-600 hover:bg-[#eef2fc] hover:text-[#2952C4] disabled:opacity-50 disabled:cursor-not-allowed" title="Descargar gráfico como PDF" data-id="element-1930">
                        {isDownloadingPie ? <div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-1931" /> : <DownloadIcon className="w-3.5 h-3.5" data-id="element-1932" />}
                        <span className="hidden sm:inline" data-id="element-1933">PDF</span>
                      </button>
                    </div>
                    <div className="mt-6 h-64" ref={pieChartRef} data-id="element-1934">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-1935">
                        <PieChart data-id="element-1936">
                          <Pie data={visitsByType} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value" data-id="element-1937">
                            {visitsByType.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1938" />)}
                          </Pie>
                          <Tooltip formatter={(value: number, name: string) => [`${value} visitas`, name]} contentStyle={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    fontSize: '13px'
                  }} data-id="element-1939" />
                          <Legend verticalAlign="bottom" height={36} formatter={value => <span className="text-sm text-slate-600" data-id="element-1941">
                                {value}
                              </span>} data-id="element-1940" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>}
              </div>
            </div>}

          {/* Main content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-stagger-3" data-id="element-1942">
            <div className="lg:col-span-2 space-y-6" data-id="element-1943">
              {canSeeVisits && <Card padding="none" data-id="element-1944">
                  <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center" data-id="element-1945">
                    <CardHeader title="Visitas Recientes" subtitle="Últimas actividades registradas" data-id="element-1946" />
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-1947">
                    {recentVisits.length === 0 ? <div className="p-12 text-center animate-fade-in" data-id="element-1948">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1949">
                          <MapPinIcon className="w-8 h-8 text-slate-400" data-id="element-1950" />
                        </div>
                        <p className="text-slate-500 font-medium" data-id="element-1951">
                          No hay visitas registradas
                        </p>
                      </div> : recentVisits.map(visit => <div key={visit.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-1952">
                          <div className="flex items-start justify-between" data-id="element-1953">
                            <div className="flex-1" data-id="element-1954">
                              <div className="flex items-center gap-2" data-id="element-1955">
                                <h4 className="font-semibold text-slate-900 font-heading" data-id="element-1956">
                                  {visit.clienteNombre}
                                </h4>
                                {getStatusBadge(visit.estado)}
                              </div>
                              <p className="text-sm text-slate-500 mt-1" data-id="element-1957">
                                {isAdmin && <span className="font-medium" data-id="element-1958">
                                    {visit.vendedorNombre} ·{' '}
                                  </span>}
                                {visit.zona} ·{' '}
                                {getVisitTypeLabel(visit.tipoVisita)}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-sm" data-id="element-1959">
                                <span className="text-slate-500" data-id="element-1960">
                                  <ClockIcon className="w-4 h-4 inline mr-1" data-id="element-1961" />
                                  {formatTime(visit.horaLlegada)} -{' '}
                                  {formatTime(visit.horaSalida)}
                                  {visit.tiempoTotal && <span className="ml-1 text-slate-400" data-id="element-1962">
                                      ({visit.tiempoTotal}m)
                                    </span>}
                                </span>
                                {canSeeCollections && visit.cobro.huboCobo && ((visit.cobro as any)?.anulado ? <span className="inline-flex items-center gap-1 text-slate-400 font-semibold text-xs line-through" data-id="element-1963">
                                      Cobro:{' '}
                                      {formatCurrency(visit.cobro.montoCobrado)}
                                      <span className="no-underline ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px] font-bold" data-id="element-1964">
                                        ANULADO
                                      </span>
                                    </span> : <span className="text-emerald-600 font-semibold" data-id="element-1965">
                                      Cobro:{' '}
                                      {formatCurrency(visit.cobro.montoCobrado)}
                                    </span>)}
                                {canSeeSales && visit.venta.huboVenta && <span className="font-semibold" style={{
                        color: '#2952C4'
                      }} data-id="element-1966">
                                    Venta:{' '}
                                    {formatCurrency(visit.venta.montoVenta)}
                                  </span>}
                              </div>
                            </div>
                          </div>
                        </div>)}
                  </div>
                </Card>}

              {/* Pending Orders Widget - only if user has pedidos permission */}
              {canSeePedidos && <Card padding="none" data-id="element-1967">
                  <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center" data-id="element-1968">
                    <div className="flex items-center gap-3" data-id="element-1969">
                      <div className="p-2 bg-blue-50 rounded-lg" data-id="element-1970">
                        <TruckIcon className="w-5 h-5 text-blue-600" data-id="element-1971" />
                      </div>
                      <div data-id="element-1972">
                        <h3 className="font-bold text-slate-900 font-heading" data-id="element-1973">
                          Pedidos Pendientes
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-1974">
                          Aprobar o cancelar órdenes
                        </p>
                      </div>
                    </div>
                    {newOrderAlert && <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 border border-amber-200 rounded-full text-xs font-bold text-amber-700 animate-pulse" data-id="element-1975">
                        <BellIcon className="w-3.5 h-3.5" data-id="element-1976" />
                        Nuevo pedido
                      </span>}
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-1977">
                    {pendingOrders.length === 0 ? <div className="px-6 py-10 text-center animate-fade-in" data-id="element-1978">
                        <CheckCircleIcon className="w-10 h-10 text-emerald-300 mx-auto mb-2" data-id="element-1979" />
                        <p className="text-sm text-slate-400 font-medium" data-id="element-1980">
                          Sin pedidos pendientes
                        </p>
                      </div> : pendingOrders.slice(0, 5).map(order => <div key={order.id} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-1981">
                          <div className="flex-1 min-w-0" data-id="element-1982">
                            <h4 className="font-semibold text-slate-900 truncate font-heading" data-id="element-1983">
                              {order.farmacia}
                            </h4>
                            <p className="text-sm text-slate-500 mt-0.5" data-id="element-1984">
                              {new Date(order.fecha).toLocaleDateString('es-DO', {
                      day: 'numeric',
                      month: 'short'
                    })}
                              {' · '}
                              {order.numeroPedido}
                              {' · '}
                              <span className="font-medium text-slate-700" data-id="element-1985">
                                {formatCurrency(order.total)}
                              </span>
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1986">
                            <button onClick={() => handleApproveOrder(order.id)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-1987">
                              <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1988" />
                              Aprobar
                            </button>
                            <button onClick={() => handleCancelOrder(order.id)} className="flex items-center gap-1 px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-semibold rounded-lg transition-colors" data-id="element-1989">
                              <XCircleIcon className="w-3.5 h-3.5" data-id="element-1990" />
                              Cancelar
                            </button>
                          </div>
                        </div>)}
                  </div>
                  {orders.filter(o => o.estado !== 'pendiente').slice(0, 3).length > 0 && <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50" data-id="element-1991">
                      <p className="text-xs font-semibold text-slate-500 mb-2" data-id="element-1992">
                        Recientes
                      </p>
                      <div className="space-y-1.5" data-id="element-1993">
                        {orders.filter(o => o.estado !== 'pendiente').slice(0, 3).map(order => <div key={order.id} className="flex items-center justify-between text-sm" data-id="element-1994">
                              <span className="text-slate-600 truncate" data-id="element-1995">
                                {order.farmacia} · {order.numeroPedido}
                              </span>
                              <Badge variant={order.estado === 'entregado' ? 'success' : order.estado === 'cancelado' ? 'danger' : order.estado === 'enviado' ? 'info' : 'warning'} data-id="element-1996">
                                {order.estado.charAt(0).toUpperCase() + order.estado.slice(1)}
                              </Badge>
                            </div>)}
                      </div>
                    </div>}
                </Card>}
            </div>

            <div className="space-y-6" data-id="element-1997">
              {canSeeEquipo && <Card data-id="element-1998">
                  <CardHeader title="Equipo Comercial" data-id="element-1999" />
                  <div className="mt-4 space-y-3" data-id="element-2000">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-2001">
                      <div className="flex items-center gap-3" data-id="element-2002">
                        <UsersIcon className="w-5 h-5 text-slate-500" data-id="element-2003" />
                        <span className="text-sm text-slate-600" data-id="element-2004">
                          Vendedores Activos
                        </span>
                      </div>
                      <span className="font-bold text-slate-900" data-id="element-2005">
                        {activeVendors.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl" data-id="element-2006">
                      <div className="flex items-center gap-3" data-id="element-2007">
                        <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-2008" />
                        <span className="text-sm text-slate-600" data-id="element-2009">
                          Visitas Completadas
                        </span>
                      </div>
                      <span className="font-bold text-emerald-600" data-id="element-2010">
                        {stats.visitasCompletadas}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl" data-id="element-2011">
                      <div className="flex items-center gap-3" data-id="element-2012">
                        <AlertCircleIcon className="w-5 h-5 text-amber-600" data-id="element-2013" />
                        <span className="text-sm text-slate-600" data-id="element-2014">
                          En Progreso
                        </span>
                      </div>
                      <span className="font-bold text-amber-600" data-id="element-2015">
                        {stats.visitasEnProgreso}
                      </span>
                    </div>
                  </div>
                </Card>}

              {canSeeProductividad && <Card data-id="element-2016">
                  <CardHeader title="Productividad" data-id="element-2017" />
                  <div className="mt-4" data-id="element-2018">
                    <div className="flex items-center justify-between mb-2" data-id="element-2019">
                      <span className="text-sm text-slate-500" data-id="element-2020">
                        Meta diaria
                      </span>
                      <span className="text-sm font-semibold text-slate-900" data-id="element-2021">
                        {stats.totalVisitas} / 8 visitas
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3" data-id="element-2022">
                      <div className="h-3 rounded-full transition-all duration-500" style={{
                  width: `${Math.min(stats.totalVisitas / 8 * 100, 100)}%`,
                  backgroundColor: '#2952C4'
                }} data-id="element-2023" />
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-sm" data-id="element-2024">
                      <TrendingUpIcon className="w-4 h-4 text-emerald-500" data-id="element-2025" />
                      <span className="text-slate-600" data-id="element-2026">
                        {stats.totalVisitas >= 8 ? '¡Meta alcanzada!' : `Faltan ${8 - stats.totalVisitas} visitas`}
                      </span>
                    </div>
                  </div>
                </Card>}

              {stats.visitasEnProgreso > 0 && <Card className="border-amber-200 bg-amber-50" data-id="element-2027">
                  <div className="flex items-start gap-3" data-id="element-2028">
                    <div className="p-2 bg-amber-100 rounded-xl" data-id="element-2029">
                      <MapPinIcon className="w-5 h-5 text-amber-600" data-id="element-2030" />
                    </div>
                    <div data-id="element-2031">
                      <h4 className="font-semibold text-slate-900" data-id="element-2032">
                        Visita en Progreso
                      </h4>
                      <p className="text-sm text-slate-600 mt-1" data-id="element-2033">
                        Tienes una visita activa. Recuerda finalizarla al salir
                        del cliente.
                      </p>
                    </div>
                  </div>
                </Card>}
            </div>
          </div>
        </> : null}
    </div>;
}